import { Instagram, Facebook, MapPin, Phone, Mail, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";

export const Footer = ({ content }) => {
  const c = content || {};
  const mapEmbed = c.map_query
    ? `https://www.google.com/maps?q=${encodeURIComponent(c.map_query)}&output=embed`
    : "";

  return (
    <footer
      id="kontakt"
      data-testid="site-footer"
      className="relative bg-sm-inverse text-sm-bg pt-24 md:pt-32 pb-10"
    >
      <div className="sm-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <div className="aspect-[16/10] w-full overflow-hidden border border-white/10">
              {mapEmbed && (
                <iframe
                  title="San Martino 26 Map"
                  data-testid="footer-map"
                  src={mapEmbed}
                  className="w-full h-full grayscale-[40%] contrast-110"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              )}
            </div>
          </Reveal>

          <div className="lg:col-span-5 lg:pl-6">
            <Reveal>
              <p className="text-[0.7rem] tracking-[0.32em] uppercase text-sm-terra">
                {c.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="footer-title"
                className="font-serif text-4xl md:text-5xl text-sm-bg mt-6 leading-[1] italic font-light"
              >
                {c.title}
              </h2>
            </Reveal>

            <Reveal delay={0.2}>
              <ul className="mt-12 space-y-7">
                <li className="flex gap-5">
                  <MapPin className="w-4 h-4 text-sm-terra mt-1 shrink-0" />
                  <div>
                    <p className="text-[0.65rem] tracking-[0.32em] uppercase text-white/60 mb-1">
                      Adresa
                    </p>
                    <p data-testid="footer-address" className="text-sm-bg leading-relaxed">
                      {c.address_line_1}
                      <br />
                      {c.address_line_2}
                    </p>
                  </div>
                </li>
                <li className="flex gap-5">
                  <Clock className="w-4 h-4 text-sm-terra mt-1 shrink-0" />
                  <div>
                    <p className="text-[0.65rem] tracking-[0.32em] uppercase text-white/60 mb-1">
                      Orari
                    </p>
                    <p
                      data-testid="footer-hours"
                      className="text-sm-bg leading-relaxed font-mono text-sm"
                    >
                      {c.hours_lunch}
                      <br />
                      {c.hours_dinner}
                    </p>
                  </div>
                </li>
                <li className="flex gap-5">
                  <Phone className="w-4 h-4 text-sm-terra mt-1 shrink-0" />
                  <div>
                    <p className="text-[0.65rem] tracking-[0.32em] uppercase text-white/60 mb-1">
                      Telefon
                    </p>
                    <a
                      href={`tel:${(c.phone || "").replace(/\s/g, "")}`}
                      data-testid="footer-phone"
                      className="text-sm-bg sm-link-underline"
                    >
                      {c.phone}
                    </a>
                  </div>
                </li>
                <li className="flex gap-5">
                  <Mail className="w-4 h-4 text-sm-terra mt-1 shrink-0" />
                  <div>
                    <p className="text-[0.65rem] tracking-[0.32em] uppercase text-white/60 mb-1">
                      Email
                    </p>
                    <a
                      href={`mailto:${c.email}`}
                      data-testid="footer-email"
                      className="text-sm-bg sm-link-underline"
                    >
                      {c.email}
                    </a>
                  </div>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-12 flex items-center gap-4">
                {c.instagram_url && (
                  <a
                    href={c.instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-instagram"
                    aria-label="Instagram"
                    className="w-10 h-10 border border-white/30 flex items-center justify-center hover:bg-sm-bg hover:text-sm-text transition-colors duration-500"
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
                {c.facebook_url && (
                  <a
                    href={c.facebook_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-facebook"
                    aria-label="Facebook"
                    className="w-10 h-10 border border-white/30 flex items-center justify-center hover:bg-sm-bg hover:text-sm-text transition-colors duration-500"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 items-center justify-between text-[0.7rem] tracking-[0.2em] uppercase text-white/50">
          <p>© {new Date().getFullYear()} San Martino 26 · Trattoria Moderne</p>
          {c.tagline && (
            <p className="font-serif italic normal-case tracking-normal text-white/40">
              "{c.tagline}"
            </p>
          )}
          <a
            href="/admin"
            data-testid="footer-admin-link"
            className="text-white/40 hover:text-white/70 transition-colors"
          >
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
